import { useLanguage } from '../../context/LanguageContext';
import { t } from '../../lib/translations';
import {
  ZUS_OWNER_ENTITIES, PARTNER_ENTITIES, ZUS_PERSONS_ENTITIES,
  EMPLOYEE_RATE, ZUS_OWNER_RATE, ZUS_PERSON_RATE, PARTNER_RATE, KOMANDYTOWA_PARTNER_RATE,
  calculateEmployeeCost, calculateZusCost,
} from '../../lib/pricingLogic';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Users, ShieldCheck, Minus, Plus } from 'lucide-react';

function CounterInput({ value, onChange, min = 0, max = 99, testId }) {
  return (
    <div className="flex items-center gap-2">
      <button
        onClick={() => onChange(Math.max(min, value - 1))}
        className="w-9 h-9 rounded-lg border border-slate-300 flex items-center justify-center hover:bg-slate-50 transition-colors"
        data-testid={`${testId}-minus`}
      >
        <Minus className="w-4 h-4 text-slate-600" />
      </button>
      <Input
        type="number"
        value={value}
        onChange={(e) => onChange(Math.max(min, Math.min(max, parseInt(e.target.value) || 0)))}
        min={min}
        max={max}
        className="w-20 text-center font-mono-price font-bold h-9 border-slate-300"
        data-testid={testId}
      />
      <button
        onClick={() => onChange(Math.min(max, value + 1))}
        className="w-9 h-9 rounded-lg border border-slate-300 flex items-center justify-center hover:bg-slate-50 transition-colors"
        data-testid={`${testId}-plus`}
      >
        <Plus className="w-4 h-4 text-slate-600" />
      </button>
    </div>
  );
}

export default function Step3Employees({ formData, updateField }) {
  const { language } = useLanguage();

  const showZusOwner = ZUS_OWNER_ENTITIES.includes(formData.legalEntity);
  const showPartners = PARTNER_ENTITIES.includes(formData.legalEntity);
  const showZusPersons = ZUS_PERSONS_ENTITIES.includes(formData.legalEntity);
  const isKomandytowa = formData.legalEntity === 'spolka_komandytowa';

  const employeeCost = calculateEmployeeCost(formData.employeeCount);
  const zusCost = calculateZusCost(formData);

  const totalPersons = formData.employeeCount +
    (formData.zusOwner ? 1 : 0) +
    (formData.partnerCount || 0) +
    (formData.zusPersonsCount || 0);

  return (
    <div data-testid="step-3-employees">
      <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1B3A7B] mb-2">
        {t('step3.title', language)}
      </h2>
      <p className="text-slate-500 mb-8 text-base">
        {t('step3.subtitle', language)}
      </p>

      <div className="max-w-lg mx-auto space-y-4">
        {/* Employees */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#1B3A7B] text-white flex items-center justify-center">
                <Users className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800 text-sm">{t('step3.employeeCount', language)}</h3>
                <p className="text-xs text-slate-500">{t('step3.employeeHint', language)}</p>
              </div>
            </div>
            <CounterInput
              value={formData.employeeCount}
              onChange={(v) => updateField('employeeCount', v)}
              testId="employee-count"
            />
          </div>
          {formData.employeeCount > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-100 flex justify-end">
              <span className="font-mono-price text-sm font-semibold text-[#E8941A]">
                {formData.employeeCount} × {EMPLOYEE_RATE} = {employeeCost} {t('common.currency', language)}
              </span>
            </div>
          )}
        </div>

        {/* ZUS Owner */}
        {showZusOwner && (
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800 text-sm">{t('step3.zusOwner', language)}</h3>
                  <p className="text-xs text-slate-500">{t('step3.zusOwnerHint', language)}</p>
                </div>
              </div>
              <Switch
                checked={formData.zusOwner}
                onCheckedChange={(v) => updateField('zusOwner', v)}
                data-testid="zus-owner-switch"
              />
            </div>
            {formData.zusOwner && (
              <div className="mt-3 pt-3 border-t border-slate-100 flex justify-end">
                <span className="font-mono-price text-sm font-semibold text-[#E8941A]">
                  {ZUS_OWNER_RATE} {t('common.currency', language)}/{t('common.perMonth', language)}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Partners */}
        {showPartners && (
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center">
                  <Users className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800 text-sm">{t('step3.partnerCount', language)}</h3>
                  <p className="text-xs text-slate-500">{t('step3.partnerHint', language)}</p>
                </div>
              </div>
              <CounterInput
                value={formData.partnerCount}
                onChange={(v) => updateField('partnerCount', v)}
                max={20}
                testId="partner-count"
              />
            </div>
            {formData.partnerCount > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-100 flex justify-end">
                <span className="font-mono-price text-sm font-semibold text-[#E8941A]">
                  {formData.partnerCount} × {isKomandytowa ? KOMANDYTOWA_PARTNER_RATE : PARTNER_RATE} = {formData.partnerCount * (isKomandytowa ? KOMANDYTOWA_PARTNER_RATE : PARTNER_RATE)} {t('common.currency', language)}
                </span>
              </div>
            )}
          </div>
        )}

        {/* ZUS Persons */}
        {showZusPersons && (
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800 text-sm">{t('step3.zusPersons', language)}</h3>
                  <p className="text-xs text-slate-500">{t('step3.zusPersonsHint', language)}</p>
                </div>
              </div>
              <CounterInput
                value={formData.zusPersonsCount}
                onChange={(v) => updateField('zusPersonsCount', v)}
                max={50}
                testId="zus-persons-count"
              />
            </div>
            {formData.zusPersonsCount > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-100 flex justify-end">
                <span className="font-mono-price text-sm font-semibold text-[#E8941A]">
                  {formData.zusPersonsCount} × {ZUS_PERSON_RATE} = {formData.zusPersonsCount * ZUS_PERSON_RATE} {t('common.currency', language)}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Summary */}
        <div className="bg-slate-50 rounded-xl border border-slate-200 p-5 flex items-center justify-between">
          <span className="text-sm text-slate-600">
            {t('step3.totalPersons', language)}: <strong className="font-mono-price">{totalPersons}</strong> {t('step3.persons', language)}
          </span>
          <span className="font-mono-price text-xl font-bold text-[#E8941A]">
            {employeeCost + zusCost} {t('common.currency', language)}
          </span>
        </div>
      </div>
    </div>
  );
}
